<template>
  <div class="cart-thresholds critical-component-clear">
    <p
      v-if="progressText"
      class="cart-thresholds__text text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      :style="{ visibility: codeState === 'loading' ? 'hidden' : 'visible' }"
      v-html="progressText"
    />

    <div class="cart-thresholds__bar-background">
      <div class="cart-thresholds__bar" :style="{ width: `${progress}%` }" />

      <span
        v-for="threshold of thresholdsOnly"
        :key="threshold.handle"
        class="cart-thresholds__marker"
        :class="{
          [$class('active')]: isIconActive(threshold.active),
          'is-freebie': threshold.type !== 'shipping',
        }"
        :style="{ left: getMarkerPosition(threshold.threshold) }"
      >
        <div v-if="threshold.type === 'shipping'">
          <icon-box />
        </div>
        <div v-else>
          <responsive-image
            class="cart-thresholds__freebie"
            :image="getFreebieImage(threshold)"
            ratio="1:1"
            sizes="36px"
          />
        </div>
      </span>
    </div>

    <p
      v-if="standardShipping"
      class="cart-thresholds__text-bottom text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      v-html="standardShipping"
    />
  </div>
</template>

<script>
/**
 * Vue: Cart thresholds (cart-thresholds)
 * -----------------------------------------------------------------------------
 * Indicator to show progress to cart thresholds for free shipping and freebies.
 * - Excluded line items are handled in the cart Pinia store getter.
 *
 * @param {Number} standardShippingRate - Standard shipping rate price.
 *
 */
import { mapState } from 'pinia';
import useCartStore from '~stores/cart';
import { dateTime } from '~/helpers/general';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import IconBox from '~icons/general/box.svg';
import ResponsiveImage from '~global/images/responsive-image.vue';

export default {
  name: 'CartThresholds',

  components: {
    IconBox,
    ResponsiveImage,
  },

  props: {
    standardShippingRate: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      campaignThresholds: 'thresholds',
      activeCampaign: 'active',
      codeState: 'codeState',
      isFreeShipping: 'freeShipping',
    }),

    ...mapState(useCartStore, { cartTotal: 'getIncludedCartTotal' }),

    /**
     * Get free shipping threshold.
     * - Based on active campaign or metafield and country code.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     * @returns {Number}
     */
    freeShippingThreshold() {
      // if there is a free shipping code active, we always have free shipping
      if (this.isFreeShipping) {
        return 0;
      }
      const campaignShippingThreshold = dateTime(
        this.activeCampaign?.shipping?.startDate,
        this.activeCampaign?.shipping?.endDate,
      )
        ? this.activeCampaign?.shipping?.discounts?.find((discount) => {
            return discount.countryCode === cnvs.store.country;
          })?.threshold
        : null;

      // check is done as the threshold can be 0 and 0 is considered falsy
      return typeof campaignShippingThreshold === 'number'
        ? campaignShippingThreshold * 100
        : cnvs.discount.shopFreeShipping * 100;
    },

    availableThresholds() {
      const shippingThreshold = this.isFreeShipping
        ? [
            {
              active: true,
              handle: 'free-shipping',
              remaining: 0,
              threshold: 0,
              type: 'shipping',
            },
          ]
        : [
            {
              active: this.cartTotal >= this.freeShippingThreshold,
              handle: 'free-shipping',
              remaining: this.freeShippingThreshold - this.cartTotal,
              threshold: this.freeShippingThreshold,
              type: 'shipping',
            },
          ];

      if (!this.campaignThresholds) {
        return shippingThreshold;
      }

      const filteredThresholds = this.campaignThresholds.filter((threshold) => {
        // if all products are not available we don't show the freebie
        const allSoldOut = threshold.freeProducts.every((product) => {
          const availableState = product.selectedOrFirstAvailableVariant
            ? product.selectedOrFirstAvailableVariant.availableState
            : product.availableState;
          return availableState !== 'available';
        });
        const availableProducts = allSoldOut
          ? []
          : threshold.freeProducts.filter((product) => {
              const availableState = product.selectedOrFirstAvailableVariant
                ? product.selectedOrFirstAvailableVariant.availableState
                : product.availableState;
              return (
                product.availableState !== 'hidden' &&
                !(
                  this.$variable('discount.freebies.hideSoldout') &&
                  availableState !== 'available'
                )
              );
            });
        return availableProducts.length > 0;
      });

      return [...shippingThreshold, ...filteredThresholds].sort(
        (a, b) => a.threshold - b.threshold,
      );
    },

    /**
     * Compute number of freebies available
     * @return {Number}
     */
    freebieCount() {
      let count = 0;

      this.availableThresholds.forEach((threshold) => {
        if (
          threshold.active &&
          (threshold.type === 'threshold' || threshold.type === 'product')
        ) {
          count += threshold.quantity;
        }
      });

      return count;
    },

    /**
     * Compute if there is free shipping based on cart total
     * @returns {Boolean}
     */
    freeShipping() {
      if (this.isFreeShipping) {
        return true;
      }

      return this.availableThresholds.some((threshold) => {
        return threshold.active && threshold.type === 'shipping';
      });
    },

    /**
     * Compute thresholds excluding product thresholds - not showing gift icon.
     * @returns {Array}
     */
    thresholdsOnly() {
      return this.availableThresholds.filter((threshold) => {
        return threshold.type !== 'product';
      });
    },

    /**
     * Compute maximum threshold for all thresholds.
     * @returns {Number}
     */
    maximumThreshold() {
      return this.availableThresholds[this.availableThresholds.length - 1]
        ?.threshold;
    },

    /**
     * Compute next threshold, of any type.
     * @returns {Object}
     */
    nextThreshold() {
      const threshold = this.availableThresholds.find(
        (object) => !object.active,
      );

      const freebie = this.availableThresholds.find((object) => {
        return !object.active && object.type === 'threshold';
      });

      const shipping = this.availableThresholds.find((object) => {
        return !object.active && object.type === 'shipping';
      });

      if (!freebie && !shipping && !threshold) {
        return {};
      }

      return {
        freebie,
        shipping,
        threshold,
      };
    },

    /**
     * Compute progress to threshold.
     * - Caps out at 100(%).
     * @returns {Number} - Percent without % sign.
     */
    progress() {
      const progress = (this.cartTotal / this.maximumThreshold) * 100;

      // empty cart with free shipping at 0 - shop empty progress
      if (this.maximumThreshold === 0 && this.cartTotal === 0) {
        return Math.min(100, 0);
      }

      // non-empty cart with free shipping at 0 - show full progress
      if (this.maximumThreshold === 0) {
        return Math.min(100, 100);
      }

      return Math.min(100, progress);
    },

    /**
     * Compute progress text.
     * - Can't use $string pluralise with '_html' plural values.
     * @returns {String}
     */
    progressText() {
      let count = this.freebieCount || 1;
      if (this.progress === 100) {
        let string = '';

        switch (this.stringType.all) {
          case 'both':
            string =
              count === 1
                ? 'cart.thresholds.reached.both_one'
                : 'cart.thresholds.reached.both_other';
            break;
          case 'freebie':
            string =
              count === 1
                ? 'cart.thresholds.reached.freebie_only_one'
                : 'cart.thresholds.reached.freebie_only_other';
            break;
          case 'shipping':
            string = 'cart.thresholds.reached.shipping_only';
            break;
        }

        return string
          ? this.$string(string, { replace: { count: this.freebieCount } })
          : '';
      }

      const freebie = this.$formatMoney(this.nextThreshold.freebie?.remaining);
      const shipping = this.isFreeShipping
        ? 0
        : this.$formatMoney(this.nextThreshold.shipping?.remaining);
      // set it to a different property as we still have a next threshold
      count = this.nextThreshold.freebie?.quantity || 1;

      let string = '';

      switch (this.stringType.next) {
        case 'both':
          string =
            count === 1
              ? 'cart.thresholds.spend_more.both_one_html'
              : 'cart.thresholds.spend_more.both_other_html';
          break;
        case 'both-additional':
          string =
            count === 1
              ? 'cart.thresholds.spend_more.both_additional_one_html'
              : 'cart.thresholds.spend_more.both_additional_other_html';
          break;
        case 'freebie':
          string =
            count === 1
              ? 'cart.thresholds.spend_more.freebie_only_one_html'
              : 'cart.thresholds.spend_more.freebie_only_other_html';
          break;
        case 'freebie-additional':
          string =
            count === 1
              ? 'cart.thresholds.spend_more.freebie_additional_only_one_html'
              : 'cart.thresholds.spend_more.freebie_additional_only_other_html';
          break;
        case 'shipping':
          string =
            this.freeShippingThreshold === 0 || this.isFreeShipping
              ? 'cart.thresholds.spend_more.shipping_only_free_html'
              : 'cart.thresholds.spend_more.shipping_only_html';
          break;
      }

      return string
        ? this.$string(string, { replace: { count, freebie, shipping } })
        : '';
    },

    /**
     * Compute standard shipping text.
     * @returns {String}
     */
    standardShipping() {
      return this.isFreeShipping
        ? this.$string('cart.thresholds.shipping_cost.free_html', {
            replace: {
              amount_classes: 'cart-thresholds__standard',
              amount: this.$formatMoney(this.standardShippingRate * 100),
              classes:
                'cart-thresholds__standard-free text-mobile-paragraph-2xs-bold text-desktop-paragraph-2xs-bold',
              free_amount: this.$formatMoney(0),
            },
          })
        : this.$string('cart.thresholds.shipping_cost.standard', {
            replace: {
              amount: this.$formatMoney(this.standardShippingRate * 100),
            },
          });
    },

    /**
     * Compute string type based on freebie and shipping thresholds.
     * @returns {String}
     */
    stringType() {
      const hasFreebies = this.availableThresholds.some((threshold) => {
        return threshold.type === 'threshold';
      });

      const hasShipping = this.availableThresholds.some((threshold) => {
        return threshold.type === 'shipping';
      });

      let all = 'both';

      if (!hasFreebies && hasShipping) {
        all = 'shipping';
      } else if (hasFreebies && !hasShipping) {
        all = 'freebie';
      }

      /**
       * Determine next threshold string type.
       */
      let next = 'both';

      if (!this.nextThreshold.freebie && this.nextThreshold.shipping) {
        next = 'shipping';
      } else if (this.nextThreshold.freebie && !this.nextThreshold.shipping) {
        next = 'freebie';
      }

      // if next is empty, we use the value from all as they should be identical
      if (Object.keys(this.nextThreshold).length === 0) {
        next = all;
      }

      /**
       * If both or freebie then determine if this is the second freebie
       * threshold as this adjusts wording.
       */
      if (['both', 'freebie'].includes(next)) {
        const previousFreebie = this.availableThresholds.some((threshold) => {
          return threshold.active && threshold.type === 'threshold';
        });

        if (previousFreebie) {
          next += '-additional';
        }
      }

      return {
        all,
        next,
      };
    },
  },

  methods: {
    /**
     * Get freebie image based on type.
     */
    getFreebieImage(threshold) {
      if (!threshold?.freeProducts?.length) {
        return null;
      }
      const byAvailableState = (fp) => {
        const variant = fp.selectedOrFirstAvailableVariant;
        return variant ? variant.availableState : fp.availableState;
      };
      // Only show image when an actually available free product exists.
      const product = threshold.freeProducts.find(
        (fp) => byAvailableState(fp) === 'available',
      );
      if (!product) {
        return null;
      }
      const variant = product.selectedOrFirstAvailableVariant;
      return variant?.featuredImage || product?.featuredImage || null;
    },
    /**
     * Compute active state of threshold to add css class
     * @param activeState - Active state of threshold.
     */
    isIconActive(activeState) {
      if (this.maximumThreshold === 0 && this.cartTotal === 0) {
        return false;
      }

      return activeState;
    },

    /**
     * Get marker % position.
     * @param {Number} threshold - Threshold amount.
     * @returns {String}
     */
    getMarkerPosition(threshold) {
      const position = (threshold / this.maximumThreshold) * 100;

      // when free shipping at 0 then always show full progress bar
      if (threshold === 0 && this.maximumThreshold === 0) {
        return '100%';
      }

      return `${position}%`;
    },
  },
};
</script>

<style lang="scss">
@import './cart-thresholds';
</style>
